import Button from 'components/base/Button';
import AuthSocialButtons from 'components/common/AuthSocialButtons';
import { Col, Form, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  registerSchema,
  RegisterSchema
} from 'pages/pages/authentication/card/model';
import { checkEmailAvailability, registerUser } from 'api/auth/_requests';
import toast from 'react-hot-toast';

const SignUpForm = ({ layout }: { layout: 'simple' | 'card' | 'split' }) => {
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue
  } = useForm<RegisterSchema>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      role: 'customer'
    }
  });

  const onSubmit = async (data: RegisterSchema) => {
    const res = await checkEmailAvailability(data.email);
    if (res?.available) {
      await registerUser(data).then(res => {
        toast.success('Account created successfully');
        navigate('/sign-in');
      });
    } else {
      toast.error('Email is already in use');
    }
  };

  return (
    <>
      <h3 className="text-body-highlight">Sign Up</h3>
      <p className="text-body-tertiary">Create your account today</p>
      <AuthSocialButtons title="Sign up" />
      <div className="position-relative mt-4">
        <hr className="bg-body-secondary" />
        <div className="divider-content-center">or use email</div>
      </div>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Form.Group className="mb-3 text-start">
          <Row>
            <Col className="text-center">
              <Button
                variant={
                  watch('role') === 'vendor' ? 'success' : 'outline-success'
                }
                onClick={() => setValue('role', 'vendor')}
                className="w-100 fs-9 text-nowrap"
              >
                Be a Vendor
              </Button>
            </Col>
            <Col className="text-center">
              <Button
                variant={
                  watch('role') === 'customer' ? 'success' : 'outline-success'
                }
                onClick={() => setValue('role', 'customer')}
                className="w-100 fs-9 text-nowrap"
              >
                Be a Customer
              </Button>
            </Col>
          </Row>
        </Form.Group>

        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="fullName">Name</Form.Label>
          <Form.Control
            id="fullName"
            type="text"
            placeholder="Name"
            {...register('fullName')}
            isInvalid={!!errors.fullName}
          />
          {errors.fullName && (
            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.fullName.message}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Form.Group className="mb-3 text-start">
          <Form.Label htmlFor="email">Email address</Form.Label>
          <Form.Control
            id="email"
            type="email"
            placeholder="name@example.com"
            {...register('email')}
            isInvalid={!!errors.email}
          />
          {errors.email && (
            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.email.message}
            </Form.Control.Feedback>
          )}
        </Form.Group>
        <Row className="g-3 mb-3">
          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="password">Password</Form.Label>
              <Form.Control
                id="password"
                type="password"
                placeholder="Password"
                {...register('password')}
                isInvalid={!!errors.password}
              />
              {errors.password && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.password.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
          <Col sm={layout === 'card' ? 12 : 6} lg={6}>
            <Form.Group>
              <Form.Label htmlFor="confirmPassword">
                Confirm Password
              </Form.Label>
              <Form.Control
                id="confirmPassword"
                type="password"
                placeholder="Confirm Password"
                {...register('confirmPassword')}
                isInvalid={!!errors.confirmPassword}
              />
              {errors.confirmPassword && (
                <Form.Control.Feedback type="invalid" className="d-block">
                  {errors.confirmPassword.message}
                </Form.Control.Feedback>
              )}
            </Form.Group>
          </Col>
        </Row>
        <Form.Check type="checkbox" className="mb-3">
          <Form.Check.Input
            type="checkbox"
            id="termsService"
            {...register('termsService')}
            isInvalid={!!errors.termsService}
          />
          <Form.Check.Label
            htmlFor="termsService"
            className="fs-9 text-transform-none"
          >
            I accept the <Link to="#!">terms </Link>and{' '}
            <Link to="#!">privacy policy</Link>
          </Form.Check.Label>
          {errors.termsService && (
            <Form.Control.Feedback type="invalid" className="d-block">
              {errors.termsService.message}
            </Form.Control.Feedback>
          )}
        </Form.Check>
        <Button
          type="submit"
          variant="primary"
          className="w-100 mb-3"
          disabled={
            !!errors.fullName ||
            !!errors.email ||
            !!errors.password ||
            !!errors.confirmPassword ||
            !!errors.termsService
          }
        >
          Sign up
        </Button>
        <div className="text-center">
          <Link
            to={`/pages/authentication/${layout}/sign-in`}
            className="fs-9 fw-bold"
          >
            Sign in to an existing account
          </Link>
        </div>
      </Form>
    </>
  );
};

export default SignUpForm;
